import React from "react";
import { TableRowActionsBody, type TableRowActionsBodyProps } from "./TableRowActionsBody";
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import type { BaseEntity } from "../../types";

export type TableRowActionsWrapperProps<TData extends BaseEntity> = Omit<
  TableRowActionsBodyProps<TData>,
  "updateMutation" | "deleteMutation" | "filterActionsByRole"
>;

/**
 * Standalone row actions: subscribes to the entity's update + delete mutations
 * itself. Use this from surfaces that render entity rows/cards OUTSIDE a
 * `SimpleTable` (drilldown modals, dashboard cards, detail-page panels — see
 * ENTITY_TABLE_ROW_ACTIONS).
 *
 * INSIDE a `SimpleTable`, `RowActions` renders `TableRowActionsBody` directly
 * with the table's single shared mutation pair instead — this component's two
 * hooks used to run once per row, and with ~25 visible rows plus
 * `CRM_VIRTUAL_OVERSCAN` that was ~70 mutation-cache subscriptions from this
 * file alone.
 */
export function TableRowActionsWrapper<TData extends BaseEntity>(
  props: TableRowActionsWrapperProps<TData>
) {
  // CRITICAL: hooks must be called unconditionally and before any early return.
  const updateMutation = props.hooks.useUpdate();
  const deleteMutation = props.hooks.useDelete();
  const { filterActionsByRole } = useActionFiltering();

  return (
    <TableRowActionsBody<TData>
      {...props}
      updateMutation={updateMutation}
      deleteMutation={deleteMutation}
      filterActionsByRole={filterActionsByRole}
    />
  );
}
